import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { loCodeAPI, entryAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Download, FileSpreadsheet, FileText, Loader2, RefreshCw, BookOpen, Check, ArrowRight, ChevronUp, ChevronDown, Edit } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const SUBJECTS = ['Mathematics', 'Science', 'English', 'Odia', 'Hindi', 'Sanskrit', 'History', 'Geography'];
const CLASSES = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'];
const SECTIONS = ['Written', 'Oral', 'Both'];
const EXAMS = ['SA-I', 'SA-II', 'FA-1', 'FA-2', 'FA-3', 'FA-4'];
const YEARS = ['2024-25', '2025-26', '2026-27'];

export default function BlueprintBuilder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loCodes, setLoCodes] = useState([]);
  const [blueprint, setBlueprint] = useState([]);
  const [currentEntryId, setCurrentEntryId] = useState(null);
  
  // LO Code Library Modal
  const [showLOModal, setShowLOModal] = useState(false);
  const [loLibrary, setLoLibrary] = useState([]);
  const [selectedLOs, setSelectedLOs] = useState([]);
  const [loLibraryLoading, setLoLibraryLoading] = useState(false);
  
  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const [config, setConfig] = useState({
    subject: 'Mathematics',
    class_name: 'IV',
    exam: 'SA-II',
    year: '2025-26'
  });

  const [newItem, setNewItem] = useState({
    qLabel: '',
    section: 'Written',
    loCode: '',
    marks: 1
  });

  // Load LO codes when subject/class changes
  useEffect(() => {
    const fetchLOCodes = async () => {
      try {
        const response = await loCodeAPI.getAll(config.subject, config.class_name);
        setLoCodes(response.data || []);
      } catch (error) {
        console.error('Failed to load LO codes:', error);
      }
    };
    fetchLOCodes();
  }, [config.subject, config.class_name]);

  // Load existing entry if any
  const loadExistingEntry = useCallback(async () => {
    setLoading(true);
    try {
      const response = await entryAPI.getAll();
      const entries = response.data || [];
      
      // Find entry matching current config
      const existingEntry = entries.find(e => 
        e.class_name === config.class_name && 
        e.subject === config.subject &&
        e.exam === config.exam &&
        e.year === config.year
      );
      
      if (existingEntry) {
        setCurrentEntryId(existingEntry.id);
        const items = JSON.parse(existingEntry.items || '[]');
        setBlueprint(items);
      } else {
        setCurrentEntryId(null);
        setBlueprint([]);
      }
    } catch (error) {
      console.error('Failed to load entries:', error);
    } finally {
      setLoading(false);
    }
  }, [config.class_name, config.subject, config.exam, config.year]);

  useEffect(() => {
    loadExistingEntry();
  }, [loadExistingEntry]);

  const addItem = () => {
    if (!newItem.qLabel || !newItem.loCode || newItem.marks <= 0) {
      toast.error('Please fill all fields');
      return;
    }

    const item = {
      id: `bp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...newItem,
      loCode: newItem.loCode.toUpperCase()
    };

    setBlueprint([...blueprint, item]);
    setNewItem({ qLabel: '', section: 'Written', loCode: '', marks: 1 });
    toast.success('Item added');
  };

  const removeItem = (id) => {
    setBlueprint(blueprint.filter(item => item.id !== id));
  };

  const moveItemUp = (index) => {
    if (index === 0) return;
    const newBlueprint = [...blueprint];
    [newBlueprint[index - 1], newBlueprint[index]] = [newBlueprint[index], newBlueprint[index - 1]];
    setBlueprint(newBlueprint);
  };

  const moveItemDown = (index) => {
    if (index === blueprint.length - 1) return;
    const newBlueprint = [...blueprint];
    [newBlueprint[index], newBlueprint[index + 1]] = [newBlueprint[index + 1], newBlueprint[index]];
    setBlueprint(newBlueprint);
  };

  // Edit item
  const openEditModal = (item) => {
    setEditingItem({ ...item });
    setShowEditModal(true);
  };

  const saveEditedItem = () => {
    if (!editingItem) return;
    setBlueprint(blueprint.map(item => 
      item.id === editingItem.id ? editingItem : item
    ));
    setShowEditModal(false);
    setEditingItem(null);
    toast.success('Item updated');
  };

  // Download blueprint as Excel (admin template format)
  const downloadBlueprint = () => {
    if (blueprint.length === 0) {
      toast.error('No items to download');
      return;
    }

    const wsData = [
      ['Question Label', 'Section', 'LO Code', 'Marks'],
      ...blueprint.map(item => [
        item.qLabel,
        item.section,
        item.loCode,
        item.marks
      ])
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 8 }];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Blueprint');
    
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `Blueprint_${config.class_name}_${config.subject}_${config.exam}.xlsx`);
    
    toast.success('Blueprint downloaded');
  };

  const saveBlueprint = async () => {
    if (blueprint.length === 0) {
      toast.error('Add at least one item to the blueprint');
      return;
    }

    setSaving(true);
    try {
      const entryData = {
        class_name: config.class_name,
        subject: config.subject,
        exam: config.exam,
        year: config.year,
        items: JSON.stringify(blueprint),
        students: JSON.stringify([]),
        total_students: 0,
        total_marks_possible: blueprint.reduce((sum, item) => sum + item.marks, 0)
      };

      if (currentEntryId) {
        await entryAPI.update(currentEntryId, entryData);
        toast.success('Blueprint updated successfully!');
      } else {
        const response = await entryAPI.create(entryData);
        setCurrentEntryId(response.data.id);
        toast.success('Blueprint saved successfully!');
      }
      return true;
    } catch (error) {
      toast.error('Failed to save blueprint');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveAndNext = async () => {
    const success = await saveBlueprint();
    if (success) {
      toast.success('Blueprint saved! Now add students.');
      navigate('/dashboard/students');
    }
  };

  // Download Excel Template
  const downloadTemplate = () => {
    const templateData = [
      ['Q.Label', 'Section', 'LO Code', 'Marks'],
      ['1(a)', 'Written', 'M401', 2],
      ['1(b)', 'Written', 'M402', 2],
      ['2', 'Oral', 'M403', 1],
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    ws['!cols'] = [{ wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 8 }];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Blueprint');
    
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'blueprint_import_template.xlsx');
    
    toast.success('Template downloaded');
  };

  // Excel Import
  const handleExcelImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const imported = [];
        // Skip header row
        const startRow = jsonData[0]?.some(cell => 
          typeof cell === 'string' && (cell.toLowerCase().includes('label') || cell.toLowerCase().includes('lo'))
        ) ? 1 : 0;

        for (let i = startRow; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row && row.length >= 3) {
            const qLabel = String(row[0] || '').trim();
            const section = String(row[1] || 'Written').trim();
            const loCode = String(row[2] || '').trim().toUpperCase();
            const marks = parseFloat(row[3]) || 1;
            
            if (qLabel && loCode) {
              imported.push({
                id: `bp_${Date.now()}_${i}_${Math.random().toString(36).substr(2, 9)}`,
                qLabel,
                section: SECTIONS.includes(section) ? section : 'Written',
                loCode,
                marks
              });
            }
          }
        }

        if (imported.length > 0) {
          setBlueprint([...blueprint, ...imported]);
          toast.success(`Imported ${imported.length} items from Excel`);
        } else {
          toast.error('No valid blueprint data found in file');
        }
      } catch (error) {
        toast.error('Failed to parse Excel file');
        console.error(error);
      }
    };
    reader.readAsArrayBuffer(file);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Load LO Codes from Library
  const openLOLibrary = async () => {
    setShowLOModal(true);
    setLoLibraryLoading(true);
    setSelectedLOs([]);
    try {
      const response = await loCodeAPI.getAll(config.subject, config.class_name);
      setLoLibrary(response.data || []);
    } catch (error) {
      toast.error('Failed to load LO codes library');
      console.error(error);
    } finally {
      setLoLibraryLoading(false);
    }
  };

  const toggleLOSelection = (loId) => {
    setSelectedLOs(prev => 
      prev.includes(loId) 
        ? prev.filter(id => id !== loId)
        : [...prev, loId]
    );
  };

  const selectAllLOs = () => {
    if (selectedLOs.length === loLibrary.length) {
      setSelectedLOs([]);
    } else {
      setSelectedLOs(loLibrary.map(lo => lo.id));
    }
  };

  const addSelectedLOsToBlueprint = () => {
    if (selectedLOs.length === 0) {
      toast.error('Select at least one LO code');
      return;
    }

    const selectedLoObjects = loLibrary.filter(lo => selectedLOs.includes(lo.id));
    const newItems = selectedLoObjects.map((lo, index) => ({
      id: `bp_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`,
      qLabel: `Q${blueprint.length + index + 1}`,
      section: 'Written',
      loCode: lo.lo_code,
      marks: 1
    }));

    setBlueprint([...blueprint, ...newItems]);
    setShowLOModal(false);
    toast.success(`Added ${newItems.length} LO codes to blueprint. Edit question labels and marks as needed.`);
  };

  const totalMarks = blueprint.reduce((sum, item) => sum + Number(item.marks), 0);
  const writtenMarks = blueprint.filter(i => i.section !== 'Oral').reduce((sum, i) => sum + Number(i.marks), 0);
  const oralMarks = blueprint.filter(i => i.section !== 'Written').reduce((sum, i) => sum + Number(i.marks), 0);
  const uniqueLOs = [...new Set(blueprint.map(i => i.loCode))].length;

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-navy">Blueprint Builder</h1>
          <p className="text-slate-600 mt-1 text-sm md:text-base">Map questions to Learning Outcome codes</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline"
            onClick={loadExistingEntry}
            size="sm"
          >
            <RefreshCw className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button 
            variant="outline"
            onClick={openLOLibrary}
            size="sm"
            className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
          >
            <BookOpen className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Load LO Codes</span>
          </Button>
          <Button 
            variant="outline"
            onClick={downloadTemplate}
            size="sm"
          >
            <Download className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Template</span>
          </Button>
          <Button 
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            size="sm"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Import</span>
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".xlsx,.xls,.csv"
            onChange={handleExcelImport}
          />
          <Button 
            variant="outline"
            onClick={downloadBlueprint}
            disabled={blueprint.length === 0}
            size="sm"
            className="bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
          >
            <FileText className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Export</span>
          </Button>
          <Button 
            onClick={saveBlueprint} 
            disabled={saving || blueprint.length === 0}
            variant="outline"
            size="sm"
            data-testid="save-blueprint-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 md:mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-1 md:mr-2" />}
            <span className="hidden sm:inline">Save</span>
          </Button>
          <Button 
            onClick={saveAndNext} 
            disabled={saving || blueprint.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            size="sm"
            data-testid="save-next-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 md:mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-1 md:mr-2" />}
            <span className="hidden sm:inline">Save & Next</span>
          </Button>
        </div>
      </div>

      {/* Configuration */}
      <Card className="mb-4 md:mb-6">
        <CardHeader className="p-3 md:p-6">
          <CardTitle className="text-base md:text-lg">Report Configuration</CardTitle>
        </CardHeader>
        <CardContent className="p-3 md:p-6 pt-0 md:pt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <div>
              <Label className="text-xs md:text-sm">Class</Label>
              <Select value={config.class_name} onValueChange={(v) => setConfig({...config, class_name: v})}>
                <SelectTrigger data-testid="select-class" className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs md:text-sm">Subject</Label>
              <Select value={config.subject} onValueChange={(v) => setConfig({...config, subject: v})}>
                <SelectTrigger data-testid="select-subject" className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUBJECTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs md:text-sm">Exam</Label>
              <Select value={config.exam} onValueChange={(v) => setConfig({...config, exam: v})}>
                <SelectTrigger data-testid="select-exam" className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXAMS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs md:text-sm">Year</Label>
              <Select value={config.year} onValueChange={(v) => setConfig({...config, year: v})}>
                <SelectTrigger data-testid="select-year" className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="text-lg md:text-2xl font-bold text-navy">{blueprint.length}</div>
            <div className="text-xs md:text-sm text-slate-500">Questions</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="text-lg md:text-2xl font-bold text-navy">{totalMarks}</div>
            <div className="text-xs md:text-sm text-slate-500">Total Marks</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="text-lg md:text-2xl font-bold text-navy">{writtenMarks}/{oralMarks}</div>
            <div className="text-xs md:text-sm text-slate-500">Written/Oral</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="text-lg md:text-2xl font-bold text-navy">{uniqueLOs}</div>
            <div className="text-xs md:text-sm text-slate-500">Unique LOs</div>
          </CardContent>
        </Card>
      </div>

      {/* Add new item */}
      <Card className="mb-4 md:mb-6">
        <CardHeader className="p-3 md:p-6">
          <CardTitle className="text-base md:text-lg">Add Question</CardTitle>
        </CardHeader>
        <CardContent className="p-3 md:p-6 pt-0 md:pt-0">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 items-end">
            <div>
              <Label className="text-xs md:text-sm">Q. Label</Label>
              <Input
                placeholder="e.g., 1(a)"
                value={newItem.qLabel}
                onChange={(e) => setNewItem({...newItem, qLabel: e.target.value})}
                className="text-sm"
                data-testid="input-qlabel"
              />
            </div>
            <div>
              <Label className="text-xs md:text-sm">Section</Label>
              <Select value={newItem.section} onValueChange={(v) => setNewItem({...newItem, section: v})}>
                <SelectTrigger data-testid="select-section" className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SECTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs md:text-sm">LO Code</Label>
              <Input
                placeholder="e.g., M401"
                value={newItem.loCode}
                onChange={(e) => setNewItem({...newItem, loCode: e.target.value.toUpperCase()})}
                list="lo-codes-list"
                className="text-sm"
                data-testid="input-locode"
              />
              <datalist id="lo-codes-list">
                {loCodes.map(lo => (
                  <option key={lo.id} value={lo.lo_code}>{lo.lo_description}</option>
                ))}
              </datalist>
            </div>
            <div>
              <Label className="text-xs md:text-sm">Marks</Label>
              <Input
                type="text"
                inputMode="decimal"
                pattern="[0-9]*\.?[0-9]*"
                value={newItem.marks}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                    setNewItem({...newItem, marks: val === '' ? '' : parseFloat(val) || 0});
                  }
                }}
                onBlur={(e) => {
                  const val = parseFloat(e.target.value) || 1;
                  setNewItem({...newItem, marks: Math.max(0.5, Math.min(20, val))});
                }}
                placeholder="1"
                className="text-sm [appearance:textfield]"
                data-testid="input-marks"
              />
            </div>
            <Button onClick={addItem} className="bg-emerald-600 hover:bg-emerald-700 col-span-2 md:col-span-1" data-testid="add-item-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Blueprint table */}
      <Card>
        <CardHeader className="p-3 md:p-6">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base md:text-lg">Blueprint Items ({blueprint.length})</CardTitle>
            {blueprint.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-red-500 hover:text-red-700 text-xs"
                onClick={() => setBlueprint([])}
              >
                Clear All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0 md:p-6 md:pt-0">
          {loading ? (
            <div className="text-center py-8 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading...
            </div>
          ) : blueprint.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 text-xs">#</TableHead>
                    <TableHead className="text-xs">Q. Label</TableHead>
                    <TableHead className="text-xs">Section</TableHead>
                    <TableHead className="text-xs">LO Code</TableHead>
                    <TableHead className="text-right text-xs">Marks</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {blueprint.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium text-xs">{index + 1}</TableCell>
                      <TableCell className="text-xs">{item.qLabel}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 rounded text-[10px] md:text-xs font-medium ${
                          item.section === 'Written' ? 'bg-blue-100 text-blue-700' :
                          item.section === 'Oral' ? 'bg-amber-100 text-amber-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {item.section}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{item.loCode}</TableCell>
                      <TableCell className="text-right text-xs">{item.marks}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openEditModal(item)}
                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 h-7 w-7 p-0"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => moveItemUp(index)}
                            disabled={index === 0}
                            className="text-slate-500 hover:text-navy hover:bg-slate-100 h-7 w-7 p-0 disabled:opacity-30"
                            title="Move Up"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => moveItemDown(index)}
                            disabled={index === blueprint.length - 1}
                            className="text-slate-500 hover:text-navy hover:bg-slate-100 h-7 w-7 p-0 disabled:opacity-30"
                            title="Move Down"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeItem(item.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 md:py-12 px-4">
              <FileText className="w-10 h-10 md:w-12 md:h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-medium text-slate-700 mb-2 text-sm md:text-base">No items yet</h3>
              <p className="text-xs md:text-sm text-slate-500">Add questions using the form above or import from Excel</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* LO Codes Library Modal */}
      <Dialog open={showLOModal} onOpenChange={setShowLOModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Load LO Codes - {config.class_name} {config.subject}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4">
            {loLibraryLoading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-navy" />
                <p className="mt-2 text-sm text-slate-500">Loading LO codes...</p>
              </div>
            ) : loLibrary.length > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={selectedLOs.length === loLibrary.length && loLibrary.length > 0}
                      onCheckedChange={selectAllLOs}
                    />
                    <span className="text-sm font-medium">Select All ({loLibrary.length})</span>
                  </label>
                  <span className="text-sm text-slate-500">{selectedLOs.length} selected</span>
                </div>
                <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
                  {loLibrary.map(lo => (
                    <label 
                      key={lo.id} 
                      className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                        selectedLOs.includes(lo.id) ? 'bg-purple-50' : ''
                      }`}
                    >
                      <Checkbox 
                        checked={selectedLOs.includes(lo.id)}
                        onCheckedChange={() => toggleLOSelection(lo.id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-navy text-sm">{lo.lo_code}</span>
                          {selectedLOs.includes(lo.id) && (
                            <Check className="w-4 h-4 text-emerald-500" />
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mt-0.5">{lo.lo_description}</p>
                        {lo.lo_description_odia && (
                          <p className="text-xs text-slate-400 mt-0.5">{lo.lo_description_odia}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-medium text-slate-700 mb-2">No LO codes available</h3>
                <p className="text-sm text-slate-500">
                  Ask your administrator to upload LO codes for {config.class_name} {config.subject}
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowLOModal(false)}>Cancel</Button>
            <Button 
              onClick={addSelectedLOsToBlueprint} 
              disabled={selectedLOs.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add {selectedLOs.length} LO Code(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Item Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Question
            </DialogTitle>
          </DialogHeader>
          {editingItem && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Question Label</Label>
                <Input 
                  value={editingItem.qLabel}
                  onChange={(e) => setEditingItem({...editingItem, qLabel: e.target.value})}
                  placeholder="e.g., 1(a)"
                />
              </div>
              <div>
                <Label>Section</Label>
                <Select value={editingItem.section} onValueChange={(v) => setEditingItem({...editingItem, section: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SECTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>LO Code</Label>
                <Input 
                  value={editingItem.loCode}
                  onChange={(e) => setEditingItem({...editingItem, loCode: e.target.value.toUpperCase()})}
                  placeholder="e.g., M401"
                />
              </div>
              <div>
                <Label>Marks</Label>
                <Input 
                  type="text"
                  inputMode="decimal"
                  value={editingItem.marks}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*\.?\d*$/.test(val)) {
                      setEditingItem({...editingItem, marks: val === '' ? '' : parseFloat(val) || 0});
                    }
                  }}
                  placeholder="1"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={saveEditedItem} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
